import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAction, useMutation, usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import type * as C from '../../../../packages/domain/src/community-admin';
import { useLab } from '../lib/service';
import { fixtures } from './sample';
export type View = 'overview' | 'ideas' | 'people' | 'voting' | 'moderation';
export type Selection = {
  kind: 'opportunity' | 'idea' | 'person' | 'session' | 'publications' | 'comments' | 'reports';
  id: string;
};
export type Detail =
  | C.CommunityIdeaDetail
  | C.CommunityPersonDetail
  | C.CommunitySessionDetail
  | C.CommunityModerationDetail
  | C.CommunityOpportunityDetail;
export interface Filters {
  search: string;
  filter: string;
  period: C.CommunityPeriod;
  genre: string;
  past: boolean;
  queueState: 'pending' | 'reviewed' | 'all';
}
export interface Action {
  kind:
    | 'archive'
    | 'assign'
    | 'configure'
    | 'close'
    | 'restrict'
    | 'grant'
    | 'approve'
    | 'reject'
    | 'resolve'
    | 'remove';
  reason: string;
  id?: string;
  projectId?: string;
  personId?: string;
  profileId?: string;
  entitlementId?: string;
  expiresAt?: number;
  cap?: number;
  suspended?: boolean;
  moderationKind?: 'publications' | 'comments' | 'reports';
  durationMs?: number;
  trendingWindowMs?: number;
  winnerAllowance?: number;
  startsAt?: number;
  endsAt?: number;
}
interface Data {
  overview: C.CommunityOverview | undefined;
  rows: (C.CommunityIdea | C.CommunityPerson | C.CommunitySession | C.CommunityModeration)[];
  detail: Detail | null | undefined;
  opportunities: C.CommunityOpportunity[];
  moreOpportunities: boolean;
  loadOpportunities: () => void;
  loading: boolean;
  more: boolean;
  loadMore: () => void;
  run: (a: Action) => Promise<unknown>;
  preview: (id: string) => Promise<string>;
}
const Context = createContext<Data | null>(null);
export const useCommunity = () => useContext(Context)!;
interface Props {
  view: View;
  filters: Filters;
  selection: Selection | null;
  children: ReactNode;
}
export function CommunityProvider(props: Props) {
  const { isDemo } = useLab();
  return isDemo ? <Demo {...props} /> : <Live {...props} />;
}
function Live({ view, filters: f, selection: s, children }: Props) {
  const overview = useQuery(
    api.communityAdmin.overview,
    view === 'overview' || view === 'voting' ? { period: f.period } : 'skip',
  );
  const args = { search: f.search, filter: f.filter, period: f.period };
  const ideas = usePaginatedQuery(
    api.communityAdmin.ideas,
    view === 'ideas' ? { ...args, genre: f.genre || undefined } : 'skip',
    { initialNumItems: 20 },
  );
  const people = usePaginatedQuery(api.communityAdmin.people, view === 'people' ? args : 'skip', {
    initialNumItems: 20,
  });
  const leaderboard = usePaginatedQuery(
    api.communityAdmin.leaderboard,
    view === 'voting' && !f.past ? {} : 'skip',
    { initialNumItems: 20 },
  );
  const sessions = usePaginatedQuery(
    api.communityAdmin.sessions,
    view === 'voting' && f.past ? args : 'skip',
    { initialNumItems: 20 },
  );
  const opportunities = usePaginatedQuery(
    api.communityAdmin.opportunities,
    view === 'voting' && f.past ? args : 'skip',
    { initialNumItems: 20 },
  );
  const opportunity = useQuery(
    api.communityAdmin.opportunityDetail,
    s?.kind === 'opportunity' ? { id: s.id as Id<'creationEntitlements'> } : 'skip',
  );
  const publications = usePaginatedQuery(
    api.communityAdmin.moderationQueue,
    view === 'moderation' && ['all', 'publications'].includes(f.filter)
      ? { kind: 'publications', search: f.search, state: f.queueState }
      : 'skip',
    { initialNumItems: 20 },
  );
  const comments = usePaginatedQuery(
    api.communityAdmin.moderationQueue,
    view === 'moderation' && ['all', 'comments'].includes(f.filter)
      ? { kind: 'comments', search: f.search, state: f.queueState }
      : 'skip',
    { initialNumItems: 20 },
  );
  const reports = usePaginatedQuery(
    api.communityAdmin.moderationQueue,
    view === 'moderation' && ['all', 'reports'].includes(f.filter)
      ? { kind: 'reports', search: f.search, state: f.queueState }
      : 'skip',
    { initialNumItems: 20 },
  );
  const queues =
    f.filter === 'all'
      ? [publications, comments, reports]
      : [f.filter === 'comments' ? comments : f.filter === 'reports' ? reports : publications];
  const moderation = {
    results: queues.flatMap((q) => q.results).sort((a, b) => b.createdAt - a.createdAt),
    status: queues.some((q) => q.status === 'LoadingFirstPage')
      ? 'LoadingFirstPage'
      : queues.some((q) => q.status === 'LoadingMore')
        ? 'LoadingMore'
        : queues.some((q) => q.status === 'CanLoadMore')
          ? 'CanLoadMore'
          : 'Exhausted',
    loadMore: (n: number) =>
      queues.filter((q) => q.status === 'CanLoadMore').forEach((q) => q.loadMore(n)),
  };
  const idea = useQuery(
    api.communityAdmin.ideaDetail,
    s?.kind === 'idea' ? { id: s.id as Id<'ideas'> } : 'skip',
  );
  const person = useQuery(
    api.communityAdmin.personDetail,
    s?.kind === 'person' ? { id: s.id as Id<'users'> } : 'skip',
  );
  const session = useQuery(
    api.communityAdmin.sessionDetail,
    s?.kind === 'session' ? { id: s.id as Id<'selectionCycles'> } : 'skip',
  );
  const item = useQuery(
    api.communityAdmin.moderationDetail,
    s && ['publications', 'comments', 'reports'].includes(s.kind)
      ? { kind: s.kind as 'publications' | 'comments' | 'reports', id: s.id }
      : 'skip',
  );
  const archive = useMutation(api.ideaAdmin.archive),
    assign = useMutation(api.ideaAdmin.assign),
    configure = useMutation(api.ideaAdmin.configure),
    close = useMutation(api.ideaAdmin.closeNow),
    restrict = useMutation(api.administration.restrict),
    grant = useMutation(api.allowances.grant),
    decide = useMutation(api.community.decidePublication),
    comment = useMutation(api.administration.moderateComment),
    resolve = useMutation(api.administration.resolve),
    url = useAction(api.storage.url);
  const list =
    view === 'ideas'
      ? ideas
      : view === 'people'
        ? people
        : view === 'moderation'
          ? moderation
          : f.past
            ? sessions
            : leaderboard;
  const { status: listStatus, results: listResults, loadMore: loadPage } = list;
  // Joined filters can exhaust a raw page without a match. Continue until a match or the end.
  useEffect(() => {
    if (view !== 'overview' && listStatus === 'CanLoadMore' && !listResults.length) loadPage(20);
  }, [view, f.past, listStatus, listResults.length, loadPage]);
  const {
    status: opportunityStatus,
    results: opportunityRows,
    loadMore: loadOpportunityPage,
  } = opportunities;
  useEffect(() => {
    if (
      view === 'voting' &&
      f.past &&
      opportunityStatus === 'CanLoadMore' &&
      !opportunityRows.length
    )
      loadOpportunityPage(20);
  }, [view, f.past, opportunityStatus, opportunityRows.length, loadOpportunityPage]);
  async function run(a: Action) {
    if (a.kind === 'archive') return archive({ ideaId: a.id as Id<'ideas'>, reason: a.reason });
    if (a.kind === 'assign')
      return assign({
        ideaId: a.id as Id<'ideas'> | undefined,
        projectId: a.id ? undefined : (a.projectId as Id<'projects'> | undefined),
        authorizedUserId: a.personId as Id<'users'>,
        replacesEntitlementId: a.entitlementId as Id<'creationEntitlements'> | undefined,
        expiresAt: a.expiresAt,
        reason: a.reason,
      });
    if (a.kind === 'configure')
      return configure({
        durationMs: a.durationMs!,
        trendingWindowMs: a.trendingWindowMs!,
        winnerAllowance: a.winnerAllowance!,
        startsAt: a.startsAt,
        endsAt: a.endsAt,
        reason: a.reason,
      });
    if (a.kind === 'close') return close({ reason: a.reason });
    if (a.kind === 'restrict')
      return restrict({
        profileId: a.profileId as Id<'profiles'>,
        suspended: !!a.suspended,
        note: a.reason,
      });
    if (a.kind === 'grant')
      return grant({
        userId: a.personId as Id<'users'>,
        cap: a.cap!,
        active: true,
        note: a.reason,
      });
    if (a.moderationKind === 'publications')
      return decide({
        publicationId: a.id as Id<'publicEpisodes'>,
        approve: a.kind === 'approve',
        note: a.reason,
      });
    if (a.moderationKind === 'comments')
      return comment({
        commentId: a.id as Id<'comments'>,
        visible: a.kind === 'approve',
        note: a.reason,
      });
    return resolve({
      reportId: a.id as Id<'reports'>,
      note: a.reason,
      remove: a.kind === 'remove',
    });
  }
  return (
    <Context.Provider
      value={{
        overview,
        opportunities: opportunities.results,
        moreOpportunities: opportunities.status === 'CanLoadMore',
        loadOpportunities: () => opportunities.loadMore(20),
        rows: list.results,
        detail:
          s?.kind === 'opportunity'
            ? opportunity
            : s?.kind === 'idea'
              ? idea
              : s?.kind === 'person'
                ? person
                : s?.kind === 'session'
                  ? session
                  : item,
        loading:
          view === 'overview' || (view === 'voting' && !f.past)
            ? !overview
            : list.status === 'LoadingFirstPage' || list.status === 'LoadingMore',
        more: list.status === 'CanLoadMore',
        loadMore: () => list.loadMore(20),
        run,
        preview: (id) => url({ assetId: id as Id<'assets'> }),
      }}
    >
      {children}
    </Context.Provider>
  );
}
let demoData: ReturnType<typeof fixtures> | undefined;
function Demo({ view, filters: f, selection: s, children }: Props) {
  const { home } = useLab();
  const [data, setData] = useState(() => (demoData ??= fixtures(home?.projects[0]?._id)));
  const since = f.period === 'all' ? 0 : Date.now() - Number(f.period) * 86400000;
  const match = (value: string) => value.toLowerCase().includes(f.search.toLowerCase());
  const ideas = data.ideas.filter(
    (i) =>
      match(`${i.title} ${i.premise} ${i.creator?.name}`) &&
      i.createdAt >= since &&
      (!f.genre || i.genre === f.genre) &&
      (f.filter === 'submitted' ||
        (f.filter === 'removed'
          ? i.removed
          : !i.removed && (f.filter === 'all' || i.status === f.filter))),
  );
  const people = data.people.filter(
    (p) =>
      match(`${p.name} ${p.handle}`) &&
      (f.filter === 'voted' ? true : p.registeredAt >= since) &&
      (f.filter === 'all' ||
        f.filter === 'new' ||
        (f.filter === 'submitted' && p.submittedIdeas > 0) ||
        (f.filter === 'no-ideas' && !p.submittedIdeas) ||
        (f.filter === 'voted' &&
          data.participation.some((v) => v.userId === p.id && v.at >= since)) ||
        (f.filter === 'not-voted' && !p.votesCast) ||
        (f.filter === 'incomplete' && !p.profileId) ||
        (f.filter === 'suspended' && p.status === 'suspended')),
  );
  const sessions = data.sessions.filter(
    (s) =>
      s.endsAt >= since &&
      match(
        `${s.winner?.title ?? 'No winner'} ${s.winner?.creator?.name} ${s.production.producer?.name}`,
      ) &&
      (f.filter === 'all' ||
        (f.filter === 'awaiting' && s.winner && !s.production.productionStarted) ||
        (f.filter === 'not-started' && ['available', 'planning'].includes(s.production.stage)) ||
        (f.filter === 'no-winner' && !s.winner) ||
        f.filter === s.production.stage),
  );
  const rows =
    view === 'ideas'
      ? ideas
      : view === 'people'
        ? people
        : view === 'moderation'
          ? data.moderation.filter(
              (m) =>
                (f.filter === 'all' || m.kind === f.filter) &&
                match(`${m.title} ${m.text}`) &&
                (f.queueState === 'all' ||
                  (f.queueState === 'pending' && ['pending', 'open'].includes(m.state)) ||
                  (f.queueState === 'reviewed' && !['pending', 'open'].includes(m.state))),
            )
          : f.past
            ? sessions
            : data.overview.leaders;
  const overview = {
    ...data.overview,
    period: f.period,
    counts: {
      registrations: data.people.filter((p) => p.registeredAt >= since).length,
      ideas: data.ideas.filter((i) => i.createdAt >= since).length,
      voters: new Set(data.participation.filter((v) => v.at >= since).map((v) => v.userId)).size,
      awaiting: data.sessions.filter(
        (s) =>
          s.endsAt >= since &&
          s.winner &&
          !s.production.productionStarted &&
          s.production.stage !== 'revoked',
      ).length,
    },
    attention: {
      expired: [
        ...data.sessions.map((s) => s.production),
        ...data.opportunities.map((o) => o.production),
      ].filter((p) => p.stage === 'expired').length,
      blocked: [
        ...data.sessions.map((s) => s.production),
        ...data.opportunities.map((o) => o.production),
      ].filter((p) => p.stage === 'blocked').length,
      notStarted: data.sessions.filter((s) =>
        ['available', 'planning'].includes(s.production.stage),
      ).length,
      moderation: data.moderation.filter((m) => ['pending', 'open'].includes(m.state)).length,
    },
  };
  let detail: Detail | null = null;
  if (s?.kind === 'opportunity') {
    const opportunity = data.opportunities.find((o) => o.id === s.id);
    if (opportunity)
      detail = {
        opportunity,
        assignments: data.assignments[opportunity.idea?.id ?? opportunity.id] ?? [],
        decisions: data.decisions[opportunity.id] ?? [],
      };
  }
  if (s?.kind === 'idea') {
    const i = data.ideas.find((i) => i.id === s.id);
    if (i)
      detail = {
        idea: i,
        production:
          data.sessions.find((c) => c.winner?.id === i.id)?.production ??
          data.opportunities.find((o) => o.idea?.id === i.id)?.production ??
          data.empty,
        session: data.sessions.find((c) => c.winner?.id === i.id) ?? null,
        decisions: data.decisions[s.id] ?? [],
        assignments: data.assignments[s.id] ?? [],
      };
  }
  if (s?.kind === 'person') {
    const p = data.people.find((p) => p.id === s.id);
    if (p)
      detail = {
        person: p,
        email: `${p.handle ?? 'signup'}@example.test`,
        allowance: data.allowances[p.id] ?? null,
        ideas: data.ideas.filter((i) => i.creator?.id === p.id),
        votes: data.participation
          .filter((v) => v.userId === p.id)
          .map((v) => ({
            idea: data.ideas.find((i) => i.id === v.ideaId) ?? null,
            at: v.at,
            sessionId: 'current',
          })),
        wins: data.sessions.filter((c) => c.winner?.creator?.id === p.id),
        opportunities: [
          ...data.sessions.map((c) => c.production),
          ...data.opportunities.map((o) => o.production),
        ].filter((o) => o.producer?.id === p.id),
        decisions: data.decisions[s.id] ?? [],
      };
  }
  if (s?.kind === 'session') {
    const c = data.sessions.find((c) => c.id === s.id);
    if (c)
      detail = {
        session: c,
        assignments: data.assignments[c.winner?.id ?? ''] ?? [],
        decisions: data.decisions[s.id] ?? [],
      };
  }
  if (s && ['publications', 'comments', 'reports'].includes(s.kind)) {
    const item = data.moderation.find((m) => m.id === s.id);
    if (item) detail = { item, decisions: data.decisions[s.id] ?? [] };
  }
  async function run(a: Action) {
    const next = structuredClone(data);
    const target = a.id ?? a.personId ?? '';
    (next.decisions[target] ??= []).unshift({
      action: a.kind,
      reason: a.reason,
      at: Date.now(),
      actor: { id: 'owner', name: 'Director', handle: null },
    });
    if (a.kind === 'archive') {
      const i = next.ideas.find((i) => i.id === a.id);
      if (i) {
        i.status = 'archived';
        i.removed = true;
        i.title = 'Removed idea';
        i.premise = '';
        i.points = null;
        i.supporters = null;
      }
    }
    if (a.kind === 'restrict') {
      const p = next.people.find((p) => p.id === a.personId);
      if (p) p.status = a.suspended ? 'suspended' : 'active';
    }
    if (a.kind === 'grant')
      next.allowances[a.personId!] = { cap: a.cap!, spent: 0, reserved: 0, active: true };
    if (a.kind === 'configure')
      next.overview.config = {
        enabled: true,
        durationMs: a.durationMs!,
        trendingWindowMs: a.trendingWindowMs!,
        winnerAllowance: a.winnerAllowance!,
        nextStartsAt: a.startsAt ?? null,
        nextEndsAt: a.endsAt ?? null,
      };
    if (a.kind === 'close' && next.overview.current) {
      next.overview.current.status = 'closing';
      next.overview.current.endsAt = Date.now();
    }
    if (a.kind === 'assign') {
      const p = next.people.find((p) => p.id === a.personId);
      if (!p || p.status !== 'active') throw new Error('Choose an active, completed account.');
      const c = next.sessions.find((c) => c.winner?.id === a.id);
      const i = next.ideas.find((i) => i.id === a.id);
      if (i) i.status = 'winner';
      const manual = next.opportunities.find(
        (o) => o.idea?.id === a.id || o.production.projectId === a.projectId,
      );
      if (manual)
        manual.production = {
          ...manual.production,
          producer: p,
          deadline: a.expiresAt ?? null,
          stage: manual.production.projectId ? 'planning' : 'available',
        };
      if (!c && !manual && i)
        next.opportunities.push({
          id: 'assigned-' + Date.now(),
          idea: i,
          production: {
            ...next.empty,
            entitlementId: 'assigned-' + Date.now(),
            producer: p,
            deadline: a.expiresAt ?? null,
            stage: 'available',
          },
        });
      next.overview.leaders = next.overview.leaders.filter((i) => i.id !== a.id);
      if (next.overview.current) next.overview.current.eligible = next.overview.leaders.length;
      if (c)
        c.production = {
          ...c.production,
          producer: p,
          deadline: a.expiresAt ?? null,
          stage: c.production.projectId ? 'planning' : 'available',
          entitlementId: 'assigned-' + Date.now(),
        };
      (next.assignments[a.id!] ??= []).unshift({
        action: 'Production assigned',
        reason: a.reason,
        at: Date.now(),
        actor: { id: 'owner', name: 'Director', handle: null },
      });
    }
    if (['approve', 'reject', 'resolve', 'remove'].includes(a.kind)) {
      const m = next.moderation.find((m) => m.id === a.id);
      if (m)
        m.state =
          a.kind === 'approve'
            ? m.kind === 'publications'
              ? 'published'
              : 'visible'
            : m.kind === 'reports'
              ? 'resolved'
              : 'removed';
    }
    demoData = next;
    setData(next);
  }
  return (
    <Context.Provider
      value={{
        overview,
        rows,
        detail,
        opportunities: data.opportunities.filter(
          (o) =>
            (f.filter === 'all' || o.production.stage === f.filter) &&
            match(`${o.idea?.title ?? o.production.seriesTitle} ${o.production.producer?.name}`),
        ),
        moreOpportunities: false,
        loadOpportunities: () => {},
        loading: false,
        more: false,
        loadMore: () => {},
        run,
        preview: async () => {
          throw new Error('Illustrative record: no private master has been uploaded.');
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}
